<template>
  <section
    v-if="contentToDisplay"
    :id="`expandable-${contentToDisplay['@id']}`"
    ref="expandableRef"
    class="expandable-details my-4"
    :data-ab-id="dataAbId"
  >
    <EditableArea
      class="expandable-details--content"
      :content="contentToDisplay"
    />
    <div
      v-show="!isExpanded"
      class="expandable-details--gradient"
    />
    <SfButton
      class="!px-0 !text-neutral-900 hover:!bg-transparent active:!bg-transparent mobile:mx-3"
      variant="tertiary"
      @click="toggleMode"
    >
      {{ isExpanded ? expandedText : collapsedText }}
    </SfButton>
  </section>
</template>

<script lang="ts" setup>
import { EditableArea } from '@magnolia/vue-editor';
import { SfButton } from '@storefront-ui/vue';

interface Props {
  contentToDisplay?: any;
  defaultMode?: string;
  collapsedText?: string;
  expandedText?: string;
  previewHeight?: string;
  modeTextColor?: string;
  dataAbId?: string;
}

defineComponent({ name: 'ExpandableComponent' });

const props = withDefaults(defineProps<Props>(), {
  contentToDisplay: '',
  defaultMode: 'collapsed',
  collapsedText: 'Show more...',
  expandedText: 'Show less...',
  modeTextColor: '#000000',
  previewHeight: '140',
});

const { defaultMode } = toRefs(props);
const mode = ref(defaultMode.value);

const expandableRef = ref();
const isExpanded = computed(() => mode.value === 'expanded');
const contentHeight = computed(() => (isExpanded.value ? 'auto' : `${props.previewHeight}px`));
const contentOverflow = computed(() => (isExpanded.value ? 'visible' : 'hidden'));

const toggleMode = () => {
  mode.value = mode.value === 'expanded' ? 'collapsed' : 'expanded';
  expandableRef.value?.scrollIntoView({ behavior: 'smooth' });
};
</script>

<style lang="scss">
.expandable-details {
  position: relative;

  &--content {
    height: v-bind(contentHeight);
    overflow-y: v-bind(contentOverflow);
  }

  &--gradient {
    height: 2rem;
    bottom: 2rem;
    position: absolute;
    width: 100%;
    pointer-events: none;
    background: linear-gradient(180deg, hsla(0, 0%, 100%, 0) 0, hsla(0, 0%, 100%, 0) 1%, white 100%);
  }

  &--toggle {
    line-height: 20px;
    color: v-bind(modeTextColor);
    cursor: pointer;
  }
}
</style>
